import { SecShape, type SecondaryDef } from '../../sim/secondary';

/** S06 — TRAÎNÉE DE SALVES. L'arme des secondes où l'on ne peut pas tirer.
 *
 *  ── Le besoin ────────────────────────────────────────────────────────────
 *  Il existe dans ce jeu des passages entiers pendant lesquels le joueur ne
 *  décide plus rien de son tir, parce qu'il n'a plus le choix de sa position :
 *
 *    · le TISSEUR est le seul ennemi qui IMPOSE une trajectoire — son mur de
 *      11 projectiles ne laisse qu'un trou, qui glisse de 90 u dans le même
 *      sens sur trois murs. On ne choisit pas où l'on va, on suit le trou ;
 *    · les phases d'esquive pure des boss durent plusieurs secondes sans
 *      présenter de cible atteignable depuis l'endroit où l'on doit être.
 *
 *  Pendant ces secondes-là, la primaire tire dans le vide et les quatre
 *  premières secondaires sont inutilisables : la Lance et le Faisceau exigent
 *  un alignement qu'on n'a pas, les Mines une anticipation qu'on n'a plus le
 *  temps de faire, les Flanqueurs une menace latérale qui n'est pas le sujet.
 *  Cette arme convertit un déplacement SUBI en dégâts.
 *
 *  ── Pourquoi c'est l'exact opposé du Faisceau bref ───────────────────────
 *  Les deux partagent la forme `Faisceau` et n'ont rien d'autre en commun.
 *  C'est la donnée seule qui les sépare, et elle les sépare complètement :
 *
 *    · le Faisceau bref tire 18 volées en 0,54 s. Bouger pendant sa fenêtre
 *      coûte la moitié des dégâts : il PUNIT le déplacement ;
 *    · la Traînée tire 12 volées sur 2,2 s. Rester immobile pendant sa fenêtre
 *      empile douze salves au même endroit, dont onze sur des cadavres : elle
 *      punit l'immobilité.
 *
 *  Deux armes qui posent au joueur la même question — « est-ce que je tiens ma
 *  ligne ? » — et attendent la réponse inverse. C'est la démonstration que la
 *  frontière moteur/contenu est au bon endroit : aucune ligne de simulation
 *  n'a été nécessaire pour obtenir une arme opposée à une arme existante.
 *
 *  ── Pourquoi deux colonnes et pas une ────────────────────────────────────
 *  À 480 u/s et 0,2 s entre deux volées, le vaisseau parcourt 96 u pendant
 *  qu'il traverse. Une colonne unique de 30 u de large laisserait donc 66 u de
 *  vide entre deux dépôts : une ligne pointillée qu'un ennemi traverse sans
 *  être touché. La paire écartée de 44 u ramène le trou à une vingtaine
 *  d'unités, et la traînée se lit — et se comporte — comme un ruban.
 *
 *  ── L'équilibrage ────────────────────────────────────────────────────────
 *  600 dégâts au total, exactement comme la Lance, mais étalés sur 2,2 s et
 *  répartis le long d'un trajet que le joueur n'a pas choisi. 100 DPS
 *  théoriques, 50 % de la primaire, et une fraction seulement se réalise :
 *  l'arme paie le trajet, pas la cible.
 *
 *  ── ⚠️ Sur le NOM DE FICHIER, qui n'est pas un détail ────────────────────
 *  Le registre numérote les secondaires dans l'ordre alphabétique des chemins
 *  ramassés par le glob, et `Snapshot` mêle l'index de l'arme équipée dans
 *  l'empreinte de replay. Un fichier ajouté AVANT `lance.def.ts` dans cet ordre
 *  décalerait donc l'index de l'arme par défaut et invaliderait la référence
 *  golden — sans qu'aucun comportement de jeu ait changé, et sans que le nom du
 *  fichier ait l'air d'avoir la moindre importance. Les deux armes ajoutées
 *  ici (`riposte`, `trainee`) se trient après `mines` pour cette seule raison. */
const trainee: SecondaryDef = {
  id: 'trainee-de-salves',
  name: 'Traînée de salves',
  shape: SecShape.Faisceau,
  // 6 s pour 2,2 s d'émission : près de quatre secondes de silence réel. Une
  // arme qui reste allumée la moitié du temps cesse d'être une décision.
  cooldown: 6,
  shots: 12,
  // 0,2 s. Douze fois plus lent que le Faisceau bref (0,03 s) : à cette période
  // on ne voit plus un trait mais douze salves distinctes, et c'est exactement
  // ce qu'il faut voir — le joueur doit pouvoir compter ce qu'il lui reste.
  shotEvery: 0.2,
  count: 2,
  spacing: 44,
  spreadDeg: 0,
  // Sous la primaire (1 800), à l'inverse de la Lance qui doit la doubler. Ici
  // rien ne sert de doubler quoi que ce soit : ce qui compte n'est pas d'aller
  // vite vers une cible, c'est d'avoir semé une salve à chaque endroit du
  // trajet. La vitesse ne fait que ramasser ce qui se trouve au-dessus.
  speed: 1600,
  radius: 15,
  damage: 25,
  text: '12 salves de 2 × 25 dégâts sur 2,2 s le long du trajet — 6 s',
};

export default trainee;
